import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "../AuthContext";
import api from "../api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Legend } from "recharts";
import "./Dashboard.css";

const fmt  = n => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtK = v => "$" + (v / 1000).toFixed(0) + "k";

// ── Pre-built period presets ──────────────────────────────────────────────────
function getPresets() {
  const today  = new Date();
  const y      = today.getFullYear();
  const m      = today.getMonth();

  const startOf = (yr, mo, day = 1) => new Date(yr, mo, day);
  const fmt8    = d => d.toISOString().split("T")[0];

  // Semi-monthly pay periods (1st-15th and 16th-end of month)
  const semiStart = today.getDate() <= 15
    ? startOf(y, m, 1)
    : startOf(y, m, 16);
  const semiEnd = today.getDate() <= 15
    ? startOf(y, m, 15)
    : new Date(y, m + 1, 0);

  // Last semi-monthly period
  const lastSemiStart = today.getDate() <= 15
    ? startOf(y, m, 16 - 31 > 0 ? 16 : new Date(y, m, 0).getDate() > 15 ? 16 : 1)
    : startOf(y, m, 1);
  const lastSemiEnd = today.getDate() <= 15
    ? new Date(y, m, 0)
    : startOf(y, m, 15);

  return [
    { label: "This Pay Period",    start: fmt8(semiStart),                    end: fmt8(semiEnd) },
    { label: "Last Pay Period",    start: fmt8(lastSemiStart),                 end: fmt8(lastSemiEnd) },
    { label: "This Month",         start: fmt8(startOf(y, m, 1)),              end: fmt8(today) },
    { label: "Last Month",         start: fmt8(startOf(y, m - 1, 1)),          end: fmt8(new Date(y, m, 0)) },
    { label: "This Quarter",       start: fmt8(startOf(y, Math.floor(m/3)*3)), end: fmt8(today) },
    { label: "Last Quarter",       start: fmt8(startOf(y, Math.floor(m/3)*3 - 3)), end: fmt8(new Date(y, Math.floor(m/3)*3, 0)) },
    { label: "Year to Date",       start: `${y}-01-01`,                        end: fmt8(today) },
    { label: "Last Year",          start: `${y-1}-01-01`,                      end: `${y-1}-12-31` },
    { label: "All Time",           start: null,                                 end: null },
  ];
}

export default function Dashboard() {
  const { user } = useAuth();
  const presets  = useMemo(() => getPresets(), []);

  const [preset, setPreset]       = useState("Year to Date");
  const [customStart, setCustomStart] = useState("");
  const [customEnd,   setCustomEnd]   = useState("");
  const [showCustom,  setShowCustom]  = useState(false);
  const [summary,     setSummary]     = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [loading,     setLoading]     = useState(true);

  const activePreset = presets.find(p => p.label === preset);

  const startDate = showCustom ? customStart : activePreset?.start;
  const endDate   = showCustom ? customEnd   : activePreset?.end;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate)   params.append("end_date",   endDate);
    const qs = params.toString() ? `?${params}` : "";

    Promise.all([
      api.get(`/loans/commissions/summary${qs}`),
      api.get(`/loans/commissions/history${qs}`)
    ]).then(([s, c]) => {
      setSummary(s.data);
      setCommissions(c.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  // Chart data - group by month
  const chartData = commissions
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .reduce((acc, c) => {
      const month = new Date(c.created_at).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
      const existing = acc.find(d => d.month === month);
      if (existing) {
        existing.commission += c.commission_amount;
        existing.loans      += 1;
      } else {
        acc.push({ month, commission: c.commission_amount, loans: 1 });
      }
      return acc;
    }, []);

  // Period label for display
  const periodLabel = showCustom
    ? (customStart && customEnd ? `${customStart} → ${customEnd}` : "Custom Range")
    : preset;

  return (
    <div className="dashboard">
      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Welcome back, {user?.full_name}</p>
        </div>
        <div className="role-badge">{user?.role?.replace("_", " ")}</div>
      </div>

      {/* ── Period Selector ───────────────────────────────────────── */}
      <div className="period-selector-card">
        <div className="period-selector-header">
          <span className="period-label-text">📅 Pay Period</span>
          <span className="period-active-label">{periodLabel}</span>
        </div>

        <div className="period-presets">
          {presets.map(p => (
            <button
              key={p.label}
              className={`preset-btn ${preset === p.label && !showCustom ? "active" : ""}`}
              onClick={() => { setPreset(p.label); setShowCustom(false); }}
            >
              {p.label}
            </button>
          ))}
          <button
            className={`preset-btn ${showCustom ? "active" : ""}`}
            onClick={() => setShowCustom(true)}
          >
            Custom Range
          </button>
        </div>

        {showCustom && (
          <div className="custom-range">
            <div className="custom-range-inputs">
              <div className="date-input-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={e => setCustomStart(e.target.value)}
                />
              </div>
              <div className="date-input-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={e => setCustomEnd(e.target.value)}
                />
              </div>
              <button className="apply-btn" onClick={load} disabled={!customStart || !customEnd}>
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Stats Grid ────────────────────────────────────────────── */}
      {loading ? (
        <div className="loading-overlay">Loading data...</div>
      ) : (
        <>
          <div className="stats-grid">
            {[
              { label: "Total Loans",      value: summary?.total_loans || 0,              color: "blue",   mono: false },
              { label: "Commission",       value: fmt(summary?.total_commission_amount),   color: "blue",   mono: true  },
              { label: "Pending",          value: fmt(summary?.pending_commissions),       color: "yellow", mono: true  },
              { label: "Approved",         value: fmt(summary?.approved_commissions),      color: "green",  mono: true  },
              { label: "Paid",             value: fmt(summary?.paid_commissions),          color: "blue",   mono: true  },
            ].map(stat => (
              <div key={stat.label} className={`stat-card stat-${stat.color}`}>
                <div className="stat-label">{stat.label}</div>
                <div className={`stat-value ${stat.mono ? "mono" : ""}`}>{stat.value}</div>
              </div>
            ))}
          </div>

          {/* ── Charts ──────────────────────────────────────────────── */}
          {chartData.length > 1 && (
            <div className="charts-row">
              <div className="chart-card chart-wide">
                <h2 className="chart-title">Commission by Month</h2>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(59,130,246,0.08)" />
                    <XAxis dataKey="month" stroke="#475569" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                    <YAxis stroke="#475569" tick={{ fill: "#94a3b8", fontSize: 11 }} tickFormatter={fmtK} />
                    <Tooltip
                      contentStyle={{ background: "#141c35", border: "1px solid rgba(59,130,246,0.3)", borderRadius: "8px", color: "#e2e8f0" }}
                      formatter={v => fmt(v)}
                    />
                    <Legend wrapperStyle={{ color: "#94a3b8", fontSize: "12px" }} />
                    <Line type="monotone" dataKey="commission" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} name="Commission" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="chart-card chart-narrow">
                <h2 className="chart-title">Loans per Month</h2>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(59,130,246,0.08)" />
                    <XAxis dataKey="month" stroke="#475569" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                    <YAxis stroke="#475569" tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: "#141c35", border: "1px solid rgba(59,130,246,0.3)", borderRadius: "8px", color: "#e2e8f0" }}
                    />
                    <Bar dataKey="loans" fill="#3b82f6" radius={[4,4,0,0]} name="Loans" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {commissions.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <p>No commission data for this period.</p>
              {preset !== "All Time" && (
                <button className="preset-btn" style={{marginTop:12}} onClick={() => { setPreset("All Time"); setShowCustom(false); }}>
                  View All Time
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
